import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';

interface IUserDetails {
  login: string;
  expires_in: number;
  profile_image_url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public userDetails?: IUserDetails;
  private expirationTime: number = -1;

  get token(): string {
    if (environment.auth.bypassAuthentication) {
      return environment.auth.bypassToken;
    }
    
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      return storedToken;
    }
    this.router.navigate(['/login']);
    return '';
  }

  constructor(private router: Router, private http: HttpClient) {
    // Restore state from localStorage
    const storedUserDetails = localStorage.getItem('user_details');
    const storedExpirationTime = localStorage.getItem('expiration_time');
    if (storedUserDetails) {
      this.userDetails = JSON.parse(storedUserDetails);
    }
    if (storedExpirationTime) {
      this.expirationTime = parseInt(storedExpirationTime, 10);
    }

    // If bypass authentication is enabled and we don't have user details, set them up
    if (environment.auth.bypassAuthentication && !this.userDetails) {
      this.setupBypassAuth();
    }
  }

  private setupBypassAuth(): void {
    this.userDetails = {
      login: 'dev-user',
      expires_in: 999999
    };
    this.expirationTime = Date.now() + 999999 * 1000;
    localStorage.setItem('user_details', JSON.stringify(this.userDetails));
    localStorage.setItem('expiration_time', this.expirationTime.toString());
    localStorage.setItem('auth_token', environment.auth.bypassToken);
  }

  login(): void {
    const clientId = environment.auth.twitchClientId;
    const redirectUri = encodeURIComponent(window.location.origin + '/auth-callback');
    const twitchAuthUrl = `${environment.auth.twitchAuthUrl}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=user:read:email`;

    window.location.href = twitchAuthUrl;
  }

  handleAuthCallback(): void {
    // If bypass authentication is enabled, just navigate to home
    if (environment.auth.bypassAuthentication) {
      this.setupBypassAuth();
      this.router.navigate(['/']);
      return;
    }

    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      const params = new URLSearchParams(hash.replace('#', ''));
      const token = params.get('access_token');
      if (token) {
        localStorage.setItem('auth_token', token); // Persist token
        this.http.get<IUserDetails>(`${environment.api.baseUrl}/auth/userDetails`, {
          headers: { Authorization: `Bearer ${token}` }
        }).subscribe({
          next: (userDetails) => {
            this.userDetails = userDetails;
            this.expirationTime = Date.now() + userDetails.expires_in;
            localStorage.setItem('user_details', JSON.stringify(userDetails)); // Persist user details
            localStorage.setItem('expiration_time', this.expirationTime.toString()); // Persist expiration time
            this.router.navigate(['/']);
          },
          error: (error) => {
            if (error.status === 401) {
              this.logout(); // Token expired or invalid
              return;
            }
            if (error.status === 403) {
              this.forbidden(error.error.login); // Forbidden access
              return;
            }
            console.log('unhandled auth error', error);
          }
        });
      }
    }
  }

  logout(): void {
    this.clearLogin();
    this.router.navigate(['/login']);
  }

  forbidden(twitchLogin: string): void {
    console.log('forbidden', twitchLogin);
    this.clearLogin();
    this.router.navigate(['/forbidden'], { state: { twitchLogin } });
  }

  clearLogin(): void {
    this.expirationTime = -1;
    this.userDetails = undefined;
    localStorage.removeItem('auth_token'); // Clear token
    localStorage.removeItem('user_details'); // Clear user details
    localStorage.removeItem('expiration_time'); // Clear expiration time
  }

  isLoggedIn(): boolean {
    if (environment.auth.bypassAuthentication) {
      return true;
    }
    return !!this.userDetails && !!this.token && Date.now() < this.expirationTime;
  }
}
